import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Controller } from 'react-hook-form';
import type { Control, UseFormRegister, UseFormSetValue, UseFormGetValues, UseFormWatch } from 'react-hook-form';
import type { CuppingFormData } from '../../../hooks/inventory-hooks/useCuppingProfileForm';
import { RoastProfileSlider, ScoreSlider, CascadingCheckboxGroup, NotesField } from './CuppingFormFields';
import { MAIN_TASTES, MOUTHFEEL_FULL_OPTIONS, DEFECT_OPTIONS } from './CuppingFormConstants';
import { SCACuppingSection } from './SCACuppingSection';
import { SimplifiedSection } from './SimplifiedSection';
import { Lock, Unlock } from 'lucide-react';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';
import { useLanguage } from '../../../features/language/useLanguage';
import { useSubscription } from '../../../hooks/useSubscription';

export const CuppingModeSelector: React.FC<{
  mode: 'Simplified' | 'Full' | 'SCA';
  setMode: (value: 'Simplified' | 'Full' | 'SCA') => void;
  readOnly?: boolean;
}> = ({ mode, setMode, readOnly }) => {
  const { t } = useLanguage();
  const { isFree } = useSubscription();
  // PRD 76: Free tier may only use Simplified. Descriptive (Full) and
  // Affective (SCA) render disabled with an upgrade-hint tooltip.
  const lockedForFree = isFree;
  const upgradeHint = t('cupping.modes.upgradeRequired');
  const renderPaidModeButton = (
    targetMode: 'Full' | 'SCA',
    label: string,
    testId: string,
  ) => {
    const isLocked = lockedForFree && !readOnly;
    const isDisabled = readOnly || isLocked;
    return (
      <button
        type="button"
        data-test-id={testId}
        onClick={() => !isDisabled && setMode(targetMode)}
        disabled={isDisabled}
        title={isLocked ? upgradeHint : undefined}
        className={clsx(
          'relative inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md transition-all',
          mode === targetMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
          (readOnly || isLocked) && 'cursor-not-allowed opacity-75',
          isLocked && 'opacity-60',
        )}
      >
        {isLocked && <Lock className="h-3 w-3" aria-hidden="true" />}
        {label}
      </button>
    );
  };
  return (
    <div className="flex items-center bg-gray-100 rounded-lg p-1">
      <button
        type="button"
        data-test-id="cupping-mode-simplified"
        onClick={() => !readOnly && setMode('Simplified')}
        disabled={readOnly}
        className={clsx(
          "px-3 py-1.5 text-xs font-bold rounded-md transition-all",
          mode === 'Simplified' ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
          readOnly && "cursor-not-allowed opacity-75"
        )}
      >
        {t('cupping.formSections.mode.simplified')}
      </button>
      {renderPaidModeButton('Full', t('cupping.formSections.mode.descriptive'), 'cupping-mode-full')}
      {renderPaidModeButton('SCA', t('cupping.formSections.mode.affective'), 'cupping-mode-sca')}
    </div>
  );
};

export const CuppingMetaFields: React.FC<{
  register: UseFormRegister<CuppingFormData>;
  readOnly?: boolean;
}> = ({ register, readOnly }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-6">
      <RoastProfileSlider register={register} name="roastLevel" disabled={readOnly} />
      <div>
        <label className="block text-[10px] font-bold text-gray-500 uppercase">{t('reports.general.table.date')}</label>
        <input
          type="date"
          {...register('cuppingDate')}
          disabled={readOnly}
          onClick={openNativeDatePickerSafely}
          onKeyDown={preventManualDateEntry}
          onPaste={preventManualDatePasteOrDrop}
          onDrop={preventManualDatePasteOrDrop}
          className={clsx(
            "block w-full text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500 p-1",
            readOnly && "bg-gray-100 cursor-not-allowed"
          )}
        />
      </div>
    </div>
  );
};

export const CuppingFormSections: React.FC<{
  control: Control<CuppingFormData>;
  register: UseFormRegister<CuppingFormData>;
  setValue: UseFormSetValue<CuppingFormData>;
  getValues: UseFormGetValues<CuppingFormData>;
  watch: UseFormWatch<CuppingFormData>;
  maxScore: number;
  mode: 'Simplified' | 'Full' | 'SCA';
  readOnly?: boolean;
  scaScore?: number;
  enableSectionLocks?: boolean;
  defaultSectionsLocked?: boolean;
  isActive?: boolean;
}> = ({
  control,
  register,
  setValue,
  getValues,
  watch,
  maxScore,
  mode,
  readOnly,
  scaScore,
  enableSectionLocks,
  defaultSectionsLocked,
  isActive,
}) => {
  const { t } = useLanguage();
  const locksEnabled = !!enableSectionLocks;
  const defaultLocked = defaultSectionsLocked ?? true;

  const [lockedSections, setLockedSections] = useState<Record<string, boolean>>({});

  const resolvedLockedSections = isActive === false ? {} : lockedSections;

  const isSectionLocked = (key: string) => {
    if (!locksEnabled) return false;
    return resolvedLockedSections[key] ?? defaultLocked;
  };

  const toggleSectionLock = (key: string) => {
    if (readOnly) return;
    if (!locksEnabled) return;
    setLockedSections((prev) => {
      const current = prev[key] ?? defaultLocked;
      return { ...prev, [key]: !current };
    });
  };

  const sectionReadOnly = (key: string) => {
    return !!readOnly || isSectionLocked(key);
  };

  const renderSectionLock = (key: string, className?: string) => {
    if (!locksEnabled) return null;
    const locked = isSectionLocked(key);
    return (
      <button
        type="button"
        disabled={!!readOnly}
        onClick={() => toggleSectionLock(key)}
        className={clsx(
          'inline-flex items-center gap-2 px-2 py-1 rounded-md border text-[11px] font-medium transition-colors',
          locked
            ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
            : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100',
          readOnly && 'opacity-80 cursor-not-allowed hover:bg-inherit',
          className,
        )}
      >
        {locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
        <span>{locked ? t('cupping.formSections.unlockSection') : t('cupping.formSections.lockSection')}</span>
      </button>
    );
  };

  if (mode === 'Simplified') {
    return (
      <SimplifiedSection
        control={control}
        register={register}
        setValue={setValue}
        watch={watch}
        readOnly={readOnly}
        enableSectionLocks={locksEnabled}
        isSectionLocked={isSectionLocked}
        renderSectionLock={renderSectionLock}
      />
    );
  }
  if (mode === 'SCA') {
    return (
      <SCACuppingSection
        control={control}
        register={register}
        setValue={setValue}
        readOnly={readOnly}
        computedScore={scaScore}
        enableSectionLocks={locksEnabled}
        isSectionLocked={isSectionLocked}
        renderSectionLock={renderSectionLock}
      />
    );
  }
  return (
    <div className="flex flex-col gap-px bg-gray-200 border border-gray-200" data-test-id="cupping-full-section">
      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 grid grid-rows-[auto_auto_1fr] border-r border-gray-300">
          <div className="p-4 border-b border-gray-200">
            <ScoreSlider
              control={control}
              name="fragrance"
              label={t('cupping.formSections.labels.fragranceIntensity')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-fragrance-aroma')}
            />
          </div>
          <div className="p-4 border-b border-gray-300">
            <ScoreSlider
              control={control}
              name="aroma"
              label={t('cupping.formSections.labels.aromaIntensity')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-fragrance-aroma')}
            />
          </div>
          <div className="p-4">
            <CascadingCheckboxGroup
              name="fragrance_notes_cata"
              register={register}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              disabled={sectionReadOnly('full-fragrance-aroma')}
            />
          </div>
        </div>
        <div className="col-span-4 p-4">
          <NotesField
            register={register}
            name="notes_fragrance"
            label={t('reports.details.notes')}
            disabled={sectionReadOnly('full-fragrance-aroma')}
          />
        </div>
        {renderSectionLock('full-fragrance-aroma', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 grid grid-rows-[auto_auto_1fr] border-r border-gray-300">
          <div className="p-4 border-b border-gray-200">
            <ScoreSlider
              control={control}
              name="flavor"
              label={t('cupping.formSections.labels.flavor')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-flavor-aftertaste')}
            />
          </div>
          <div className="p-4 border-b border-gray-300">
            <ScoreSlider
              control={control}
              name="aftertaste"
              label={t('cupping.formSections.labels.aftertaste')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-flavor-aftertaste')}
            />
          </div>
          <div className="p-4">
            <CascadingCheckboxGroup 
              name="flavor_notes_cata" 
              register={register} 
              setValue={setValue} 
              getValues={getValues} 
              watch={watch}
              disabled={sectionReadOnly('full-flavor-aftertaste')}
            />
            <div className="mt-4 pt-4 border-t border-gray-100">
              <span className="text-xs font-bold text-gray-700 block mb-2">{t('cupping.formSections.labels.mainTastes')} <span className="text-xs font-light text-gray-900 italic">{t('cupping.formSections.labels.max2')}</span></span>
              <div className="flex gap-3 flex-wrap">
                {MAIN_TASTES.map(taste => {
                  const selectedTastes = watch('main_tastes') || [];
                  const isChecked = selectedTastes.includes(taste);
                  const isMaxReached = selectedTastes.length >= 2;
                  const isDisabled = sectionReadOnly('full-flavor-aftertaste') || (!isChecked && isMaxReached);

                  return (
                    <label key={taste} className={clsx("flex items-center gap-1 text-xs cursor-pointer", isDisabled && "text-gray-900 font-medium")}>
                      <input type="checkbox" value={taste} {...register('main_tastes')} disabled={isDisabled} className="rounded border-gray-300 text-gray-800 disabled:opacity-100 disabled:border-gray-500 disabled:bg-gray-200 disabled:checked:bg-gray-700" />
                      <span>{taste}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-4 p-4">
          <NotesField
            register={register}
            name="notes_flavor"
            label={t('cupping.formSections.labels.flavorAftertasteNotes')}
            disabled={sectionReadOnly('full-flavor-aftertaste')}
          />
        </div>
        {renderSectionLock('full-flavor-aftertaste', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 grid grid-rows-[auto_1fr] border-r border-gray-300">
          <div className="p-4 border-b border-gray-200">
            <ScoreSlider
              control={control}
              name="acidity"
              label={t('cupping.formSections.labels.acidity')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-acidity')}
            />
          </div>
          <div className="p-4 bg-gray-50/30">
            <p className="text-xs text-gray-400 italic">{t('cupping.formSections.labels.intensityOnly')}</p>
          </div>
        </div>
        <div className="col-span-4 p-4">
          <NotesField
            register={register}
            name="notes_acidity"
            label={t('cupping.formSections.labels.acidityNotes')}
            disabled={sectionReadOnly('full-acidity')}
          />
        </div>
        {renderSectionLock('full-acidity', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 grid grid-rows-[auto_1fr] border-r border-gray-300">
          <div className="p-4 border-b border-gray-200">
            <ScoreSlider
              control={control}
              name="sweetness"
              label={t('cupping.formSections.labels.sweetness')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-sweetness')}
            />
          </div>
          <div className="p-4 bg-gray-50/30">
            <p className="text-xs text-gray-400 italic">{t('cupping.formSections.labels.intensityOnly')}</p>
          </div>
        </div>
        <div className="col-span-4 p-4">
          <NotesField
            register={register}
            name="notes_sweetness"
            label={t('cupping.formSections.labels.sweetnessNotes')}
            disabled={sectionReadOnly('full-sweetness')}
          />
        </div>
        {renderSectionLock('full-sweetness', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 grid grid-rows-[auto_1fr] border-r border-gray-300">
          <div className="p-4 border-b border-gray-200">
            <ScoreSlider
              control={control}
              name="mouthfeel"
              label={t('cupping.formSections.labels.mouthfeel')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-mouthfeel')}
            />
          </div>
          <div className="p-4">
            {mode === 'Full' && (
              <>
                <span className="text-xs font-bold text-gray-700 block mb-2">{t('cupping.formSections.labels.attributes')}</span>
                <div className="grid grid-cols-2 gap-2">
                  {MOUTHFEEL_FULL_OPTIONS.map(opt => (
                    <label
                      key={opt}
                      className={clsx(
                        "flex items-center gap-1 text-xs cursor-pointer",
                        sectionReadOnly('full-mouthfeel') && "text-gray-900 font-medium cursor-not-allowed"
                      )}
                    >
                      <input
                        type="checkbox"
                        value={opt}
                        {...register('mouthfeel_attributes')}
                        disabled={sectionReadOnly('full-mouthfeel')}
                        className="rounded border-gray-300 text-gray-800 disabled:opacity-100 disabled:border-gray-500 disabled:bg-gray-200 disabled:checked:bg-gray-700"
                      />
                      <span className="truncate" title={opt}>{opt}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="col-span-4 p-4">
          <NotesField
            register={register}
            name="notes_mouthfeel"
            label={t('cupping.formSections.labels.mouthfeelNotes')}
            disabled={sectionReadOnly('full-mouthfeel')}
          />
        </div>
        {renderSectionLock('full-mouthfeel', 'absolute top-3 right-3')}
      </div>
      
      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 grid grid-rows-[auto_auto_1fr] border-r border-gray-300">
          <div className="p-4">
            <Controller
              control={control}
              name="qcPassed"
              render={({ field }) => {
                return (
                  <div className="flex items-start gap-2 flex-col mb-4">
                    <label className="text-xs font-bold text-gray-700 uppercase">
                      {t('cupping.formSections.labels.physicalCheckPassed')}
                    </label>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        disabled={sectionReadOnly('full-final')}
                        onClick={() => field.onChange(true)}
                        className={clsx(
                          "px-3 py-1 rounded text-xs font-bold border",
                          field.value === true
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50",
                          sectionReadOnly('full-final') && "cursor-not-allowed opacity-100 text-gray-900 bg-gray-50 font-bold"
                        )}
                      >
                        {t('cupping.formSections.labels.yes')}
                      </button>
                      <button
                        type="button"
                        disabled={sectionReadOnly('full-final')}
                        onClick={() => field.onChange(false)}
                        className={clsx(
                          "px-3 py-1 rounded text-xs font-bold border",
                          field.value === false
                            ? "bg-red-100 text-red-800 border-red-200"
                            : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50",
                          sectionReadOnly('full-final') && "cursor-not-allowed opacity-100 text-gray-900 bg-gray-50 font-bold"
                        )}
                      >
                        {t('cupping.formSections.labels.no')}
                      </button>
                    </div>
                  </div>
                );
              }}
            />

            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase">
                {t('cupping.formSections.labels.defects')}
              </label>
              <div className="flex flex-wrap gap-2">
                {DEFECT_OPTIONS.map((defect) => (
                  <label
                    key={defect}
                    className={clsx(
                      "flex items-center gap-2 text-sm text-red-700 cursor-pointer bg-red-50 px-2 py-1 rounded border border-red-100",
                      sectionReadOnly('full-final') && "cursor-not-allowed text-red-900 font-bold bg-red-100 border-red-300"
                    )}
                  >
                    <input
                      type="checkbox"
                      value={defect}
                      {...register("defects")}
                      disabled={sectionReadOnly('full-final')}
                      className="h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500 disabled:opacity-100 disabled:border-red-500 disabled:checked:bg-red-800"
                    />
                    <span>{defect}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-4 grid grid-rows-2 divide-y divide-gray-300">
          <div className="p-4">
            <ScoreSlider
              control={control}
              name="balance"
              label={t('cupping.formSections.labels.balance')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-final')}
            />
          </div>
          <div className="p-4 bg-gray-50">
            <ScoreSlider
              control={control}
              name="overall"
              label={t('cupping.formSections.labels.overall')}
              max={maxScore}
              setValue={setValue}
              disabled={sectionReadOnly('full-final')}
            />
          </div>
        </div>
        {renderSectionLock('full-final', 'absolute top-3 right-3')}
      </div>
    </div>
  );
};

export const CuppingFormFooter: React.FC<{
  readOnly?: boolean;
  onCancel?: () => void;
}> = ({ readOnly, onCancel }) => {
  const { t } = useLanguage();
  if (readOnly) {
    return (
      <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('forms.actions.close')}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-100">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
      >
        {t('auth.common.cancel')}
      </button>
      <button
        type="submit"
        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 shadow-sm"
      >
        {t('cupping.formSections.labels.saveProfile')}
      </button>
    </div>
  );
};
